<template>
  <div class="meet-container">
    
    <!-- Agora SDK Components (Hidden) -->
    <agora
        v-if="appid && token && channel && Number.isFinite(safeUid)"
        ref="ar"
        :appid="appid"
        :token="token"
        :channel="channel"
        :uid="safeUid"
        :autoStart="true"
        :errorHandler="handleError"
        @client-created="handleClientCreated"
        @join-success="handleJoinSuccess"
        @user-joined="handleUserJoin"
        @user-published="handleUserPublished"
        @user-left="handleUserLeft"
        @stream-fallback="handleStreamFallback"
        @connection-state-change="handleConnectionStateChange"
        @exception="handleSDKException"
        style="display: none;"
    >
      <agora-video-sender
          ref="videoSender"
          :cameraOff="cameraIsClosed"
          customizationPlayer
          @video-ready="handleVideoReady"
          @close="handleVideoClose"
      />
      <agora-audio-sender
          ref="audioSender"
          :mute="mute"
          @track-created="handleAudioTrackCreated"
      />
      <agora-audio-receiver ref="audioReceiver"/>
      <agora-video-receiver ref="videoReceiver" @remote-video-ready="handleRemoteVideoReady"/>
      <agora-screen-sender
          v-if="openScreenSharing"
          ref="screenAr"
          @video-ready="handleScreenVideoReady"
          @close="handleScreenVideoClose"
          @failed="handleScreenVideoFailed"
      />
    </agora>

    <!-- Main Meeting Interface -->
    <div class="meet-interface">
      <!-- Meeting Header -->
      <div class="meeting-header">
        <div class="meeting-info">
          <h3 class="meeting-title">{{ channel || 'Meeting Room' }}</h3>
          <div class="participant-count">
            <i class="fas fa-users"></i>
            <span>{{ totalParticipants }} participant{{ totalParticipants !== 1 ? 's' : '' }}</span>
          </div>
        </div>
      </div>

      <!-- Video Grid Area -->
      <div class="video-grid-container" :class="{ 'with-sidebar': showParticipantsPanel }">
        <!-- Screen Share Area (when active) -->
        <div v-if="screenShareActive" class="screen-share-area">
          <div class="screen-share-tile" v-player="screenShareDirective">
            <div class="screen-share-overlay">
              <div class="presenter-info">
                <i class="fas fa-desktop"></i>
                <span>{{ screenSharePresenter }} is presenting</span>
                <button v-if="youAreShareScreening" class="stop-sharing-btn" @click="handleShareScreen">
                  <i class="fas fa-stop"></i>
                  Stop sharing
                </button>
              </div>
            </div>
          </div>
          
          <!-- Minimized participant videos during screen share -->
          <div class="minimized-videos">
            <!-- Local video (minimized) -->
            <div class="mini-video-tile local-mini">
              <video
                v-if="!cameraIsClosed && localDirective"
                v-player="localDirective"
                class="video-player"
                autoplay
                playsinline
                muted
              ></video>
              <div class="video-overlay">
                <div class="participant-info">
                  <span class="name">You</span>
                  <div class="status-indicators">
                    <i v-if="mute" class="fas fa-microphone-slash muted"></i>
                    <i v-if="cameraIsClosed" class="fas fa-video-slash video-off"></i>
                  </div>
                </div>
                <div class="volume-indicator" v-if="!mute && localVolumeLevel > 0.1">
                  <div class="volume-bar" :style="{ width: (localVolumeLevel * 100) + '%' }"></div>
                </div>
              </div>
            </div>
            
            <!-- Remote videos (minimized) -->
            <div
                v-for="participant in allParticipants.filter(p => !p.isScreenShare)"
                :key="participant.uid"
                class="mini-video-tile"
                :class="{ 
                  'active-speaker': participant.isActiveSpeaker,
                  'pinned': participant.isPinned,
                  'video-off': !participant.hasVideo
                }"
                @click="togglePin(participant)"
            >
              <video
                v-show="participant.hasVideo && participant.videoDirective"
                v-player="participant.videoDirective"
                class="video-player"
                autoplay
                playsinline
                muted
                :key="'mini-video-' + participant.uid"
                @loadeddata="onVideoLoaded(participant.uid)"
                @error="onVideoError(participant.uid, $event)"
              ></video>
              
              <!-- Video placeholder when no video -->
              <div 
                v-show="!participant.hasVideo || !participant.videoDirective"
                class="video-placeholder">
                <div class="avatar">
                  {{ participant.displayName.charAt(0).toUpperCase() }}
                </div>
              </div>
              <div class="video-overlay">
                <div class="participant-info">
                  <span class="name">{{ participant.displayName }}</span>
                  <div class="status-indicators">
                    <i v-if="participant.isMuted" class="fas fa-microphone-slash muted"></i>
                    <i v-if="!participant.hasVideo" class="fas fa-video-slash video-off"></i>
                  </div>
                </div>
                <div class="volume-indicator" v-if="!participant.isMuted && participant.volumeLevel > 0.1">
                  <div class="volume-bar" :style="{ width: (participant.volumeLevel * 100) + '%' }"></div>
                </div>
                <button class="pin-btn" @click.stop="togglePin(participant)">
                  <i class="fas fa-thumbtack" :class="{ pinned: participant.isPinned }"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Regular Video Grid (when no screen share) -->
        <div v-else class="video-grid" :class="videoGridClass">
          <!-- Local video tile -->
          <div 
            class="video-tile local-tile"
            :class="{ 
              'active-speaker': localVolumeLevel > 0.2 && !mute,
              'video-off': cameraIsClosed,
              'pinned': localPinned
            }"
            @click="localPinned = !localPinned"
          >
            <!-- Local video player -->
            <video
              v-if="!cameraIsClosed && localDirective"
              v-player="localDirective"
              class="video-player"
              autoplay
              playsinline
              muted
            ></video>
            <div class="video-overlay">
              <div class="participant-info">
                <span class="name">You</span>
                <div class="status-indicators">
                  <i v-if="mute" class="fas fa-microphone-slash muted"></i>
                  <i v-if="cameraIsClosed" class="fas fa-video-slash video-off"></i>
                </div>
              </div>
              <div class="volume-indicator" v-if="!mute && localVolumeLevel > 0.1">
                <div class="volume-bar" :style="{ width: (localVolumeLevel * 100) + '%' }"></div>
              </div>
              <button class="pin-btn" @click.stop="localPinned = !localPinned">
                <i class="fas fa-thumbtack" :class="{ pinned: localPinned }"></i>
              </button>
            </div>
            <!-- Video off placeholder -->
            <div v-if="cameraIsClosed" class="video-placeholder">
              <div class="avatar">
                <i class="fas fa-user"></i>
              </div>
            </div>
          </div>

          <!-- Remote video tiles -->
          <div
              v-for="participant in allParticipants.filter(p => !p.isScreenShare)"
              :key="participant.uid"
              class="video-tile"
              :class="{ 
                'active-speaker': participant.isActiveSpeaker,
                'pinned': participant.isPinned,
                'video-off': !participant.hasVideo
              }"
              @click="togglePin(participant)"
          >
            <!-- Video player element with fallback -->
            <video 
              v-show="participant.hasVideo && (participant.videoDirective || participant.hasDecodeIssue)"
              v-player="participant.videoDirective"
              :ref="'remoteVideo-' + participant.uid"
              class="video-player"
              autoplay
              playsinline
              muted
              :key="'video-' + participant.uid"
              @loadeddata="onVideoLoaded(participant.uid)"
              @error="onVideoError(participant.uid, $event)"
            ></video>
            
            <!-- Direct video track fallback for decode issues -->
            <video
              v-if="participant.hasDecodeIssue && !participant.videoDirective"
              :ref="'directVideo-' + participant.uid"
              class="video-player direct-video"
              autoplay
              playsinline
              muted
              :key="'direct-video-' + participant.uid"
            ></video>
            
            <!-- Video placeholder when no video -->
            <div 
              v-show="!participant.hasVideo || !participant.videoDirective"
              class="video-placeholder">
              <div class="avatar">
                {{ participant.displayName.charAt(0).toUpperCase() }}
              </div>

              <!-- Debug info -->
<!--              <div style="font-size: 10px; color: #ccc; margin-top: 5px;" v-if="debugMode">
                hasVideo: {{ participant.hasVideo }}<br>
                hasDirective: {{ !!participant.videoDirective }}<br>
                UID: {{ participant.uid }}<br>
                Directive Type: {{ typeof participant.videoDirective }}
              </div>-->

            </div>
            <div class="video-overlay">
              <div class="participant-info">
                <span class="name">{{ participant.finalDisplayName || participant.displayName }}</span>
                <div class="status-indicators">
                  <i v-if="participant.isMuted" class="fas fa-microphone-slash muted"></i>
                  <i v-if="!participant.hasVideo" class="fas fa-video-slash video-off"></i>
                </div>
              </div>
              <div class="volume-indicator" v-if="!participant.isMuted && participant.volumeLevel > 0.1">
                <div class="volume-bar" :style="{ width: (participant.volumeLevel * 100) + '%' }"></div>
              </div>
              <button class="pin-btn" @click.stop="togglePin(participant)">
                <i class="fas fa-thumbtack" :class="{ pinned: participant.isPinned }"></i>
              </button>
            </div>
            
            <!-- Avatar fallback -->
            <div v-if="!participant.hasVideo || !participant.videoDirective" class="avatar-placeholder">
              {{ participant.displayName.charAt(0).toUpperCase() }}
            </div>
          </div>
        </div>
      </div>

      <!-- Participants Sidebar -->
      <div v-if="showParticipantsPanel" class="participants-sidebar">
        <div class="sidebar-header">
          <h4>Participants ({{ totalParticipants }})</h4>
          <button class="close-sidebar" @click="showParticipantsPanel = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="participants-list">
          <!-- Local participant -->
          <div class="participant-item local">
            <div class="participant-avatar">
              <i class="fas fa-user"></i>
            </div>
            <div class="participant-details">
              <span class="name">You</span>
              <span class="role">Host</span>
            </div>
            <div class="participant-status">
              <i v-if="mute" class="fas fa-microphone-slash muted"></i>
              <i v-else class="fas fa-microphone active"></i>
              <i v-if="cameraIsClosed" class="fas fa-video-slash video-off"></i>
              <i v-else class="fas fa-video active"></i>
            </div>
          </div>
          
          <!-- Remote participants -->
          <div
              v-for="participant in allParticipants.filter(p => !p.isScreenShare)"
              :key="participant.uid"
              class="participant-item"
          >
            <div class="participant-avatar">
              <span>{{ participant.displayName?.charAt(0)?.toUpperCase() || 'U' }}</span>
            </div>
            <div class="participant-details">
              <span class="name">{{ participant.displayName }}</span>
              <span class="role">{{ participant.isActiveSpeaker ? 'Speaking' : 'Participant' }}</span>
            </div>
            <div class="participant-status">
              <i v-if="participant.isMuted" class="fas fa-microphone-slash muted"></i>
              <i v-else class="fas fa-microphone" :class="{ active: participant.volumeLevel > 0.1 }"></i>
              <i v-if="!participant.hasVideo" class="fas fa-video-slash video-off"></i>
              <i v-else class="fas fa-video active"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Toolbar -->
      <div class="meeting-toolbar">
        <div class="toolbar-section left">
          <div class="meeting-time">{{ formatMeetingTime(meetingDuration) }}</div>
        </div>

        <div class="toolbar-section center">
          <button 
            class="control-btn mic-btn"
            :class="{ 'active': !mute, 'muted': mute }"
            @click="handleMute"
            :title="mute ? 'Turn on microphone' : 'Turn off microphone'"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path v-if="!mute" d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
              <path v-else d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
            </svg>
            <div class="btn-indicator" v-if="!mute && localVolumeLevel > 0.1"></div>
          </button>

          <button 
            class="control-btn video-btn"
            :class="{ 'active': !cameraIsClosed, 'disabled': cameraIsClosed }"
            @click="handleCamera"
            :title="cameraIsClosed ? 'Turn on camera' : 'Turn off camera'"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path v-if="!cameraIsClosed" d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
              <path v-else d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.55-.18L19.73 21 21 19.73 3.27 2z"/>
            </svg>
          </button>

          <button 
            class="control-btn more-btn"
            @click="showMoreMenu = !showMoreMenu"
            title="More options"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="2"/>
              <circle cx="19" cy="12" r="2"/>
              <circle cx="5" cy="12" r="2"/>
            </svg>
          </button>

          <button 
            class="control-btn participants-btn"
            @click="showParticipantsPanel = !showParticipantsPanel"
            :class="{ 'active': showParticipantsPanel }"
            :title="'Show everyone'"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.999 1.999 0 0 0 18.05 7h-.71c-.83 0-1.54.5-1.85 1.22l-1.92 5.78c-.25.75.16 1.58.91 1.83.75.25 1.58-.16 1.83-.91l1.25-3.75-.03.06V22h2zm-5.5-2H10v-3L7.91 6.09c-.31-.72-1.02-1.22-1.85-1.22h-.71c-.85 0-1.6.65-1.95 1.37L.86 13.87 3.36 15l2.54-7.63V22h2.1z"/>
            </svg>
            <span class="participant-count">{{ totalParticipants }}</span>
          </button>

          <button 
            class="control-btn leave-btn"
            @click="handleLeave"
            title="Leave call"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
            </svg>
          </button>
        </div>

        <div class="toolbar-section right">
          <!-- Reserved for future controls -->
        </div>
      </div>
    </div>

    <!-- Error Toast -->
    <div v-if="errorMsg" class="error-toast" @click="errorMsg = null">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ errorMsg }}</span>
      <button class="close-error" @click="errorMsg = null">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "Meet",
  props: {
    appid: {type: [String, null], required: true},
    token: {type: [String, null], required: true},
    channel: {type: [String, null], required: true},
    uid: {type: [Number, String], required: true},   // numeric uid from backend
    userAccount: {type: [String, null], default: null}, // optional display name
    preMute: {type: Boolean, default: false},
    preCameraOff: {type: Boolean, default: false}
  },
  data() {
    return {
      // Local user state
      mute: false,
      cameraIsClosed: false,
      localDirective: null,
      localVolumeLevel: 0,
      localPinned: false,
      
      // Debug mode
      debugMode: true,
      
      // Remote users and video
      remoteUsers: [],
      remoteDirectiveList: [],
      remoteVolumeLevelList: [],
      remoteMuteStatus: [],
      participantNumberMap: new Map(), // For consistent participant numbering
      
      // Screen sharing
      openScreenSharing: false,
      youAreShareScreening: false,
      shareScreenUID: null,
      screenShareDirective: null,
      screenSharePresenter: '',
      
      // Meeting state
      inMeeting: false,
      meetingStartTime: null,
      meetingDuration: 0,
      streamFallbackList: [],
      
      // UI state
      showParticipantsPanel: false,
      showMoreMenu: false,
      errorMsg: null,
      
      // Participant management
      pinnedParticipants: new Set(),
      activeSpeakerUid: null,
      activeSpeakerTimeout: null,
      isLocalVideoEnabled: true,
      isLocalAudioEnabled: true,
      blockConnection: false
    };
  },
  computed: {
    safeUid() {
      const n = Number(this.uid);
      return Number.isFinite(n) ? n : NaN;
    },
    
    // Participant management - FIXED: Enhanced UID-based video directive matching
    allParticipants() {
      if (!this.remoteUsers || !Array.isArray(this.remoteUsers)) {
        return [];
      }
      
      
      return this.remoteUsers
        .filter(user => user && user.uid != null && user.uid !== undefined)
        .map((user, userIndex) => {
        // Additional safety check
        if (!user || user.uid == null || user.uid === undefined) {
          return null;
        }
        
        // FIXED: Enhanced video directive matching by UID with better fallback
        let videoDirective = null;
        let directiveIndex = -1;
        
        // Get fresh directives from video receiver first
        const videoReceiver = this.$refs.videoReceiver;
        let currentDirectives = [];
        
        if (videoReceiver && videoReceiver.getVideoDirectiveList) {
          currentDirectives = videoReceiver.getVideoDirectiveList() || [];
          
          // Primary: Find by exact UID match
          const uidMatchIndex = currentDirectives.findIndex(directive => 
            directive && directive.uid === user.uid
          );
          
          if (uidMatchIndex >= 0) {
            videoDirective = currentDirectives[uidMatchIndex];
            directiveIndex = uidMatchIndex;
          }
        }
        
        // Fallback: Use stored directives if fresh ones not available
        if (!videoDirective && this.remoteDirectiveList && Array.isArray(this.remoteDirectiveList)) {
          const storedUidMatch = this.remoteDirectiveList.findIndex(directive => 
            directive && directive.uid === user.uid
          );
          
          if (storedUidMatch >= 0) {
            videoDirective = this.remoteDirectiveList[storedUidMatch];
            directiveIndex = storedUidMatch;
          }
        }
        
        // Update stored list with fresh directives
        if (currentDirectives.length > 0) {
          this.remoteDirectiveList = currentDirectives;
        }
        
        const hasVideo = user.hasVideo !== false;
        const hasVideoDirective = !!videoDirective;
        
        
        // FIXED: Only attempt force creation if user actually has video published
        if (!videoDirective && hasVideo) {
          this.forceCreateVideoDirectiveFor(user.uid, userIndex);
        }
        
        try {
          return {
            uid: user.uid,
            displayName: user.uid === this.shareScreenUID ? 'Screen Share' : (user.displayName || this.userAccount || `Participant ${user.uid}`),
            videoDirective: videoDirective,
            volumeLevel: this.remoteVolumeLevelList[userIndex] || 0,
            isMuted: this.remoteMuteStatus[userIndex] || false,
            hasVideo: hasVideo,
            hasVideoDirective: hasVideoDirective,
            isActiveSpeaker: this.activeSpeakerUid === user.uid,
            isPinned: this.pinnedParticipants.has(user.uid),
            isScreenShare: user.uid === this.shareScreenUID
          };
        } catch (e) {
          return null;
        }
      }).filter(participant => participant && participant !== null && participant.uid != null && participant.uid !== undefined);
    },
    
    totalParticipants() {
      if (!this.remoteUsers || !Array.isArray(this.remoteUsers)) {
        return 1; // Just local user
      }
      return this.remoteUsers.filter(u => u && u.uid != null && u.uid !== this.shareScreenUID).length + 1; // +1 for local user
    },
    
    // Video grid layout
    videoGridClass() {
      const participantCount = this.allParticipants.filter(p => !p.isScreenShare).length + 1; // +1 for local
      if (participantCount === 1) return 'grid-1';
      if (participantCount === 2) return 'grid-2';
      if (participantCount <= 4) return 'grid-4';
      if (participantCount <= 6) return 'grid-6';
      if (participantCount <= 9) return 'grid-9';
      return 'grid-many';
    },
    
    // Screen sharing state
    screenShareActive() {
      return this.openScreenSharing && (this.youAreShareScreening || this.screenShareDirective);
    }
  },
  created() {
    this.mute = this.preMute;
    this.cameraIsClosed = this.preCameraOff;
  },
  
  mounted() {
    // Start meeting timer
    this.startMeetingTimer();
    
    // Setup keyboard shortcuts
    this.setupKeyboardShortcuts();
  },
  
  beforeDestroy() {
    // Clear timers first
    if (this.activeSpeakerTimeout) {
      clearTimeout(this.activeSpeakerTimeout);
    }
    
    // Remove keyboard shortcuts
    this.removeKeyboardShortcuts();
    
    // Clear remote users to prevent null access during cleanup
    this.remoteUsers = [];
    this.remoteDirectiveList = [];
    
    // Only attempt to leave if we're actually in a meeting and have a client
    if (this.inMeeting && this.$refs.ar && typeof this.$refs.ar.leave === 'function') {
      try { 
        // Check if client exists and is in a connected state
        const client = this.$refs.ar.getClient?.();
        if (client && (client.connectionState === 'CONNECTED' || client.connectionState === 'CONNECTING')) {
          this.$refs.ar.leave();
        }
      } catch (e) { 
        // Silently ignore cleanup errors - component is being destroyed anyway
 
      }
    }
  },
  methods: {
    // Utility methods
    formatMeetingTime(seconds) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    
    startMeetingTimer() {
      this.meetingStartTime = Date.now();
      const updateTimer = () => {
        if (this.inMeeting && this.meetingStartTime) {
          this.meetingDuration = Math.floor((Date.now() - this.meetingStartTime) / 1000);
        }
        setTimeout(updateTimer, 1000);
      };
      updateTimer();
    },
    
    // Keyboard shortcuts
    setupKeyboardShortcuts() {
      this.handleKeyPress = (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        
        switch (e.key.toLowerCase()) {
          case 'm':
            e.preventDefault();
            this.handleMute();
            break;
          case 'v':
            e.preventDefault();
            this.handleCamera();
            break;
          case 's':
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              this.handleShareScreen();
            }
            break;
          case 'p':
            e.preventDefault();
            this.showParticipantsPanel = !this.showParticipantsPanel;
            break;
        }
      };
      document.addEventListener('keydown', this.handleKeyPress);
    },
    
    removeKeyboardShortcuts() {
      if (this.handleKeyPress) {
        document.removeEventListener('keydown', this.handleKeyPress);
      }
    },
    
    // Participant management
    togglePin(participant) {
      if (!participant || participant.uid == null) {
        return;
      }
      
      if (this.pinnedParticipants.has(participant.uid)) {
        this.pinnedParticipants.delete(participant.uid);
      } else {
        this.pinnedParticipants.add(participant.uid);
      }
      this.$forceUpdate(); // Force reactivity update for Set
    },
    
    updateActiveSpeaker(uid) {
      if (this.activeSpeakerTimeout) {
        clearTimeout(this.activeSpeakerTimeout);
      }
      
      this.activeSpeakerUid = uid;
      
      // Clear active speaker after 2 seconds of silence
      this.activeSpeakerTimeout = setTimeout(() => {
        this.activeSpeakerUid = null;
      }, 2000);
    },
    
    // Agora SDK event handlers
    handleError(err) {
      
      // FIXED: Enhanced null safety for error handling
      if (!err) {
        this.errorMsg = 'An unexpected error occurred. Please try again.';
        return;
      }
      
      // Handle video decode failures specifically
      if (err?.code === 1005 || err?.msg === 'RECV_VIDEO_DECODE_FAILED') {
        this.handleVideoDecodeFailure(err);
        return;
      }
      
      // Safe error message extraction
      const errorMessage = err?.message || err?.msg || err?.toString?.() || '';
      const errorCode = err?.code || '';
      
      if (errorCode === 'PERMISSION_DENIED' || errorMessage.includes('permission')) {
        this.errorMsg = 'Camera and microphone permissions are required for video calling.';
      } else if (errorCode === 'NETWORK_ERROR' || errorMessage.includes('network')) {
        this.errorMsg = 'Network connection error. Please check your internet connection.';
      } else if (errorCode === 'INVALID_PARAMS' || errorMessage.includes('invalid')) {
        this.errorMsg = 'Invalid meeting parameters. Please check the meeting details.';
      } else {
        this.errorMsg = errorMessage || 'An unexpected error occurred. Please try again.';
      }
    },

    handleVideoDecodeFailure(err) {
      
      const client = this.$refs.ar?.getClient?.();
      const failedUid = err.uid;
      
      if (client && failedUid) {
        
        // Step 1: Try to unsubscribe and resubscribe to the video stream
        setTimeout(async () => {
          try {
            const remoteUsers = client.remoteUsers;
            const failedUser = remoteUsers.find(u => u.uid == failedUid);
            
            if (failedUser && failedUser.videoTrack) {
              await client.unsubscribe(failedUser, 'video');
              
              // Wait a moment then resubscribe
              setTimeout(async () => {
                try {
                  await client.subscribe(failedUser, 'video');
                  
                  // Force video receiver refresh after resubscription
                  setTimeout(() => {
                    const videoList = this.$refs.videoReceiver?.getVideoDirectiveList?.() || [];
                    this.handleRemoteVideoReady(videoList);
                    this.$forceUpdate();
                  }, 500);
                  
                } catch (resubErr) {
                  this.tryLowStreamFallback(failedUid);
                }
              }, 1000);
            }
          } catch (unsubErr) {
            this.tryLowStreamFallback(failedUid);
          }
        }, 500);
      }
    },

    tryLowStreamFallback(uid) {
      const client = this.$refs.ar?.getClient?.();
      
      if (client) {
        try {
          // Set to low stream
          client.setRemoteVideoStreamType(uid, 1);
          
          // Try to refresh video directives
          setTimeout(() => {
            const videoList = this.$refs.videoReceiver?.getVideoDirectiveList?.() || [];
            if (videoList.length > 0) {
              this.handleRemoteVideoReady(videoList);
              this.$forceUpdate();
            } else {
              // Final fallback: disable and re-enable video receiver
              this.forceVideoReceiverRestart(uid);
            }
          }, 1000);
          
        } catch (streamErr) {
          this.forceVideoReceiverRestart(uid);
        }
      }
    },

    forceVideoReceiverRestart(uid) {
      
      // Mark the user as having decode issues
      this.remoteUsers = this.remoteUsers.map(user => {
        if (user.uid === uid) {
          return { ...user, hasDecodeIssue: true };
        }
        return user;
      });
      
      // Force complete refresh of video receiver component
      setTimeout(() => {
        if (this.$refs.videoReceiver) {
          // Destroy and recreate video receiver bindings
          this.$refs.videoReceiver.$destroy?.();
          this.$nextTick(() => {
            this.$refs.videoReceiver?.$forceUpdate?.();
            
            setTimeout(() => {
              const videoList = this.$refs.videoReceiver?.getVideoDirectiveList?.() || [];
              
              if (videoList.length > 0) {
                this.handleRemoteVideoReady(videoList);
              } else {
                // Show fallback UI
                this.showVideoFallbackMessage(uid);
              }
            }, 1000);
          });
        }
      }, 500);
    },

    showVideoFallbackMessage(uid) {
      // Try direct video track binding as final attempt
      this.tryDirectVideoBinding(uid);
    },

    async tryDirectVideoBinding(uid) {
      
      const client = this.$refs.ar?.getClient?.();
      if (!client) return;
      
      try {
        // Find the remote user
        const remoteUsers = client.remoteUsers;
        const targetUser = remoteUsers.find(u => u.uid == uid);
        
        if (targetUser && targetUser.videoTrack) {
          
          // Get the direct video element
          const videoElement = this.$refs[`directVideo-${uid}`];
          if (videoElement) {
            // Directly play the video track
            await targetUser.videoTrack.play(videoElement);
            
            // Update user state
            this.remoteUsers = this.remoteUsers.map(user => {
              if (user.uid === uid) {
                return { ...user, hasVideo: true, hasDirectBinding: true };
              }
              return user;
            });
            
            this.$forceUpdate();
          }
        } else {
          this.$message?.warning?.(`فيديو المشارك ${uid} غير متاح بسبب مشكلة تقنية`);
        }
      } catch (directErr) {
        this.$message?.warning?.(`فيديو المشارك ${uid} غير متاح بسبب مشكلة تقنية`);
      }
    },

    getParticipantDisplayName(user) {
      // If it's screen share, return screen share label
      if (user.uid === this.shareScreenUID) {
        return 'Screen Share';
      }
      
      // Priority 1: Use displayName from API if available
      if (user.displayName && user.displayName.trim() !== '') {
        return user.displayName;
      }
      
      // Priority 2: Use userAccount from API if available
      if (user.userAccount && user.userAccount.trim() !== '') {
        return user.userAccount;
      }
      
      // Priority 3: If it's the local user, use this.userAccount
      if (user.uid == this.uid && this.userAccount) {
        return this.userAccount;
      }
      
      // Fallback: Use UID-based name
      return `Participant ${user.uid}`;
    },

    getParticipantNumber(uid) {
      // Create a consistent mapping of UIDs to participant numbers
      if (!this.participantNumberMap) {
        this.participantNumberMap = new Map();
      }
      
      if (!this.participantNumberMap.has(uid)) {
        // Assign the next available number
        const existingNumbers = Array.from(this.participantNumberMap.values());
        let nextNumber = 1;
        while (existingNumbers.includes(nextNumber)) {
          nextNumber++;
        }
        this.participantNumberMap.set(uid, nextNumber);
      }
      
      return this.participantNumberMap.get(uid);
    },

    // URGENT: Validate token before connection attempts
    async validateTokenAndConnect() {
      
      if (!this.token || this.token === 'null' || this.token.length < 10) {
        this.errorMsg = 'خطأ في رمز التوثيق - يرجى إعادة تحميل الصفحة';
        return false;
      }
      
      if (!this.channel || !this.appid) {
        this.errorMsg = 'معلومات الاتصال ناقصة - يرجى إعادة تحميل الصفحة';
        return false;
      }
      
      return true;
    },

    // URGENT: Safe reconnection with validation
    async forceReconnect() {
      
      // Validate before attempting reconnection
      if (!(await this.validateTokenAndConnect())) {
        return;
      }
      
      try {
        const client = this.$refs.ar?.getClient?.();
        if (client && client.connectionState !== 'CONNECTED') {
          // Ensure we're properly disconnected first
          try {
            await client.leave();
          } catch (leaveErr) {
          }
          
          // Wait a moment then rejoin
          await new Promise(resolve => setTimeout(resolve, 1000));
          await client.join(this.token, this.channel, this.safeUid);
        }
      } catch (err) {
        console.error('[Meet] URGENT: Reconnection failed:', err);
        this.errorMsg = 'فشل في إعادة الاتصال - سيتم إعادة تحميل الصفحة';
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    },

    // URGENT: Retry subscription for failed users
    async retrySubscription(user, mediaType) {
      console.log(`[Meet] URGENT: Retrying ${mediaType} subscription for ${user.uid}`);
      const client = this.$refs.ar?.getClient?.();
      if (!client) return;

      let retryCount = 0;
      const maxRetries = 5;
      
      const retry = async () => {
        try {
          await client.subscribe(user, mediaType);
          console.log(`[Meet] URGENT: ${mediaType} subscription successful for ${user.uid} after ${retryCount} retries`);
        } catch (err) {
          retryCount++;
          if (retryCount < maxRetries) {
            console.log(`[Meet] URGENT: Retry ${retryCount}/${maxRetries} for ${user.uid} ${mediaType}`);
            setTimeout(retry, 1000 * retryCount); // Exponential backoff
          } else {
            console.error(`[Meet] URGENT: Failed to subscribe to ${mediaType} for ${user.uid} after ${maxRetries} attempts`);
          }
        }
      };
      
      retry();
    },

    // URGENT: Handle low bitrate error
    async handleLowBitrateError(uid) {
      console.log(`[Meet] URGENT: Handling low bitrate for ${uid}`);
      try {
        const client = this.$refs.ar?.getClient?.();
        if (client) {
          // Reduce video quality to maintain connection
          client.setVideoEncoderConfiguration({
            width: 320,
            height: 240,
            frameRate: 10,
            bitrateMin: 100,
            bitrateMax: 300,
            bitrate: 200
          });
          console.log('[Meet] URGENT: Reduced video quality due to low bitrate');
        }
      } catch (err) {
        console.error('[Meet] URGENT: Failed to adjust bitrate:', err);
      }
    },

    // URGENT: Enhanced invalid operation error handler with detailed diagnosis
    handleInvalidOperationError() {
      console.log('[Meet] URGENT: Analyzing invalid operation error...');
      const client = this.$refs.ar?.getClient?.();
      
      if (!client) {
        console.error('[Meet] URGENT: Client not found - cannot diagnose');
        this.errorMsg = 'خطأ في العميل - يرجى إعادة تحميل الصفحة';
        return;
      }
      
      const state = client.connectionState;
      console.log('[Meet] DETAILED DIAGNOSIS:', {
        connectionState: state,
        hasToken: !!this.token,
        tokenLength: this.token ? this.token.length : 0,
        channel: this.channel,
        uid: this.uid,
        appid: this.appid,
        clientExists: !!client
      });
      
      // Detailed error messages based on state
      if (state === 'DISCONNECTED') {
        console.log('[Meet] URGENT: Client DISCONNECTED - connection lost');
        this.errorMsg = 'انقطع الاتصال - جارٍ إعادة المحاولة...';
        this.attemptReconnection();
      } else if (state === 'DISCONNECTING') {
        console.log('[Meet] URGENT: Client DISCONNECTING - in transition');
        this.errorMsg = 'جارٍ قطع الاتصال...';
        setTimeout(() => this.checkConnectionAndReconnect(), 3000);
      } else if (state === 'CONNECTING') {
        console.log('[Meet] URGENT: Client CONNECTING - operation blocked');
        this.errorMsg = 'جارٍ الاتصال - انتظر قليلاً...';
      } else if (state === 'CONNECTED') {
        console.log('[Meet] URGENT: Client CONNECTED but operation invalid - token issue?');
        this.errorMsg = 'مشكلة في التوثيق - جارٍ إعادة المحاولة...';
        this.validateAndReconnect();
      } else {
        console.error('[Meet] URGENT: Unknown connection state:', state);
        this.errorMsg = 'حالة اتصال غير معروفة - جارٍ إعادة التحميل...';
        setTimeout(() => window.location.reload(), 3000);
      }
    },

    // URGENT: Check connection and attempt reconnection if needed
    async checkConnectionAndReconnect() {
      const client = this.$refs.ar?.getClient?.();
      if (client && client.connectionState === 'DISCONNECTED') {
        console.log('[Meet] URGENT: Connection check failed, reconnecting...');
        await this.attemptReconnection();
      } else {
        console.log('[Meet] Connection check passed');
        this.errorMsg = null;
      }
    },

    // URGENT: Validate connection parameters and reconnect
    async validateAndReconnect() {
      console.log('[Meet] URGENT: Validating connection parameters...');
      
      if (!await this.validateTokenAndConnect()) {
        this.errorMsg = 'فشل التحقق من بيانات الاتصال - يرجى إعادة تحميل الصفحة';
        return;
      }
      
      await this.attemptReconnection();
    },

    // URGENT: Attempt reconnection with proper error handling
    async attemptReconnection() {
      console.log('[Meet] URGENT: Starting reconnection attempt...');
      try {
        await this.forceReconnect();
        this.errorMsg = 'تم استعادة الاتصال بنجاح';
        setTimeout(() => { this.errorMsg = null; }, 2000);
      } catch (err) {
        console.error('[Meet] URGENT: Reconnection failed:', err);
        this.errorMsg = 'فشل في إعادة الاتصال - سيتم إعادة تحميل الصفحة';
        setTimeout(() => window.location.reload(), 3000);
      }
    },
    
    handleClientCreated() {
      console.log('[Meet] Client created successfully');
      window._client = this.$refs.ar.getClient?.();
      window._AgoraRTC = this.$refs.ar.getAgoraRtc?.();
      
      // Configure video encoding parameters to prevent decode failures
      const client = this.$refs.ar.getClient?.();
      if (client) {
        try {
          // URGENT FIX: Enable low latency mode for remote users
          client.setLowStreamParameter({
            width: 320,
            height: 240,
            frameRate: 15,
            bitrate: 200
          });
          
          // Force H.264 baseline profile for maximum compatibility
          client.setVideoEncoderConfiguration({
            width: 640,
            height: 480,
            frameRate: 15,
            bitrateMin: 300,
            bitrateMax: 1500,
            bitrate: 800,
            optimizationMode: "motion",
            codec: "h264"
          });
          
          // URGENT: Enable dual stream for better connectivity
          client.enableDualStream();
          console.log('[Meet] Enhanced connectivity configuration applied');
          
          // URGENT: Force cloud proxy for users with connectivity issues
          client.setCloudProxy(1); // Auto mode
          console.log('[Meet] Cloud proxy enabled for better connectivity');
          
        } catch (configErr) {
          console.warn('[Meet] Video configuration failed:', configErr);
        }
        // URGENT: Handle all SDK exceptions with comprehensive error handling
        client.on('exception', (evt) => {
          console.log('[Meet] SDK Exception:', evt);
          
          if (evt.code === 1005 && evt.msg === 'RECV_VIDEO_DECODE_FAILED') {
            this.handleVideoDecodeFailure(evt);
          } else if (evt.code === 1003 && evt.msg === 'SEND_VIDEO_BITRATE_TOO_LOW') {
            console.log('[Meet] URGENT: Bitrate too low, adjusting...');
            this.handleLowBitrateError(evt.uid);
          } else if (evt.msg && evt.msg.includes('INVALID_OPERATION')) {
            console.log('[Meet] URGENT: Invalid operation, checking connection state...');
            this.handleInvalidOperationError();
          } else if (evt.code === 2001 || evt.code === 2003) {
            // Audio input/bitrate too low - handled gracefully
            console.log(`[Meet] Audio quality issue (${evt.code}): ${evt.msg}`);
          } else if (evt.code === 4001 || evt.code === 4003) {
            // Audio recovered - handled gracefully  
            console.log(`[Meet] Audio quality recovered (${evt.code}): ${evt.msg}`);
          }
        });
        
        client.on('connection-state-change', (newState, prevState, reason) => {
          console.log('[Meet] Connection state change:', {
            from: prevState,
            to: newState,
            reason: reason
          });
          
          if (newState === 'DISCONNECTED') {
            this.errorMsg = 'انقطع الاتصال - جارِ إعادة المحاولة...';
            // URGENT: Auto-reconnect for remote users
            setTimeout(() => {
              this.forceReconnect();
            }, 2000);
          } else if (newState === 'CONNECTING') {
            this.errorMsg = 'جارِ الاتصال...';
          } else if (newState === 'CONNECTED') {
            this.errorMsg = null;
            console.log('[Meet] Connection restored successfully');
          } else if (newState === 'RECONNECTING') {
            this.errorMsg = 'جارِ إعادة الاتصال...';
          }
        });
        
        // URGENT: Handle user connection failures
        client.on('user-joined', (user) => {
          console.log('[Meet] URGENT: User joined, forcing subscription:', user.uid);
          // Force immediate subscription to ensure connectivity
          setTimeout(() => {
            if (user.hasVideo) {
              client.subscribe(user, 'video').catch(err => {
                console.error('[Meet] URGENT: Video subscription failed:', err);
                this.retrySubscription(user, 'video');
              });
            }
            if (user.hasAudio) {
              client.subscribe(user, 'audio').catch(err => {
                console.error('[Meet] URGENT: Audio subscription failed:', err);
                this.retrySubscription(user, 'audio');
              });
            }
          }, 500);
        });
        
        client.on('network-quality', (stats) => {
          if (stats.downlinkNetworkQuality <= 2) {
            console.warn('[Meet] Poor network quality detected:', stats);
          }
        });
      }
    },
    
    async created() {
      console.log("[Meet] Component created with props:", {
        appid: this.appid,
        token: this.token ? "present" : "missing",
        channel: this.channel,
        uid: this.uid,
        userAccount: this.userAccount
      });
      
      // URGENT: Validate all connection parameters before proceeding
      await this.preConnectionValidation();
    },

    async preConnectionValidation() {
      console.log('[Meet] URGENT: Running DETAILED pre-connection validation...');
      
      // DETAILED token analysis
      console.log('[Meet] TOKEN ANALYSIS:', {
        exists: !!this.token,
        type: typeof this.token,
        length: this.token ? this.token.length : 0,
        firstChars: this.token ? this.token.substring(0, 10) + '...' : 'none',
        isNull: this.token === 'null',
        isEmpty: this.token === ''
      });
      
      if (!this.token || this.token === 'null' || this.token === '' || this.token.length < 10) {
        this.errorMsg = '🚨 التوكن منتهي الصلاحية - يرجى إعادة تحميل الصفحة أو الحصول على رمز جديد';
        console.error('[Meet] 🚨 CRITICAL TOKEN FAILURE:', {
          tokenExists: !!this.token,
          tokenValue: this.token,
          tokenLength: this.token ? this.token.length : 0
        });
        
        // Disable all connection attempts
        this.blockConnection = true;
        return false;
      }
      
      // DETAILED connection parameters check
      console.log('[Meet] CONNECTION PARAMS:', {
        appid: this.appid,
        channel: this.channel,
        uid: this.uid,
        userAccount: this.userAccount
      });
      
      if (!this.appid || !this.channel) {
        this.errorMsg = '❌ معلومات الاتصال ناقصة';
        console.error('[Meet] ❌ CRITICAL: Missing connection parameters');
        return false;
      }
      
      if (!this.uid || this.uid === 'null') {
        this.errorMsg = '❌ معرف المستخدم غير صالح';
        console.error('[Meet] ❌ CRITICAL: Invalid UID');
        return false;
      }
      
      console.log('[Meet] ✅ VALIDATION PASSED - Ready to connect');
      return true;
    },
    
    handleJoinSuccess(echoUid) {
      console.log('[Meet] Successfully joined meeting:', {
        echoUid,
        expectedUid: this.safeUid,
        channel: this.channel,
        appid: this.appid
      });
      
      this.inMeeting = true;
      this.meetingStartTime = Date.now();
      
      // Validate the echo UID matches our expected UID
      if (echoUid !== this.safeUid) {
        console.warn('[Meet] UID mismatch: expected', this.safeUid, 'got', echoUid);
      }
      
      // Clear any previous error messages
      this.errorMsg = null;
      
      // Force check for existing video directives after join
      setTimeout(() => {
        const existingVideoList = this.$refs.videoReceiver?.getVideoDirectiveList?.() || [];
        console.log('[Meet] Post-join video directive check:', existingVideoList);
        if (existingVideoList.length > 0) {
          this.handleRemoteVideoReady(existingVideoList);
        }
      }, 100);
      
      this.$message?.success?.(this.$t?.('joined_successfully') || "تم الانضمام إلى الاجتماع بنجاح");
    },
    handleUserJoin(user) {
      // URGENT: Comprehensive null safety check
      if (!user) {
        console.warn('[Meet] URGENT: Null user object received');
        return;
      }
      
      if (user.uid == null || user.uid === undefined) {
        console.warn('[Meet] URGENT: User has null/undefined uid:', user);
        return;
      }
      
      console.log('[Meet] User joined (safe):', {
        uid: user.uid,
        hasVideo: user.hasVideo,
        hasAudio: user.hasAudio
      });
      
      const client = this.$refs.ar.getClient?.();
      const remoteUsers = this.$refs.ar?.getRemoteUsers?.() || [];
      
      // Deep filtering to ensure all users have valid uid before assignment
      this.remoteUsers = remoteUsers
        .filter(u => {
          if (!u || u.uid == null || u.uid === undefined) {
            console.warn('[Meet] Filtering out invalid remote user:', u);
            return false;
          }
          return true;
        })
        .filter(u => !this.openScreenSharing || u.uid !== this.shareScreenUID)
        .map(u => {
          try {
            return {
              uid: u.uid,
              hasVideo: u.hasVideo !== false,
              hasAudio: u.hasAudio !== false,
              displayName: u.displayName || null,
              userAccount: u.userAccount || null,
              finalDisplayName: this.getParticipantDisplayName(u)
            };
          } catch (err) {
            console.error('[Meet] Error mapping remote user:', u, err);
            return null;
          }
        })
        .filter(u => u !== null);

      // Fallback (weak network)                                                                         
      try {
        client?.setStreamFallbackOption?.(user.uid, 2);
        if (this.userAccount) client?.setStreamFallbackOption?.(this.userAccount, 2);
      } catch (e) {                                                                                             
        console.warn('[Meet] Stream fallback option failed:', e);
      }
      
      this.handleCheckRemoteUserAudioMuteStatus();
      
      // Force refresh video directives with delay to ensure library state is ready
      this.$nextTick(() => {
        setTimeout(() => {
          this.$forceUpdate();
        }, 50);
      });
      
      this.$message?.info?.(`${user.uid} joined the meeting`);
    },
    handleUserPublished(user, mediaType) {
      console.log('[Meet] User published:', user.uid, 'mediaType:', mediaType);
      
      // FIXED: Enhanced validation with better error handling
      if (mediaType === "video") {
        if (!user.hasVideo || !user.videoTrack) {
          console.warn(`[Meet] User ${user.uid} published video but hasVideo=${user.hasVideo}, videoTrack=${!!user.videoTrack}`);
          return;
        }
        
        console.log(`[Meet] Valid video publish from ${user.uid}:`, {
          hasVideo: user.hasVideo,
          hasVideoTrack: !!user.videoTrack,
          videoTrackId: user.videoTrack?.getMediaStreamTrack?.()?.id
        });
      }
      
      // Update remote users list immediately with enhanced mapping
      const remoteUsers = this.$refs.ar?.getRemoteUsers?.() || [];
      console.log('[Meet] Updated remote users after publish:', {
        totalUsers: remoteUsers.length,
        publishedUser: user.uid,
        mediaType: mediaType,
        users: remoteUsers.map(u => ({ uid: u?.uid, hasVideo: u?.hasVideo, hasAudio: u?.hasAudio }))
      });
      
      this.remoteUsers = remoteUsers
        .filter(u => u && u.uid != null && u.uid !== undefined)
        .filter(u => !this.openScreenSharing || u.uid !== this.shareScreenUID)
        .map(u => ({
          uid: u.uid,
          hasVideo: u.hasVideo !== false,
          hasAudio: u.hasAudio !== false,
          displayName: this.getParticipantDisplayName(u),
          videoTrack: u.videoTrack || null
        }));
      
      console.log('[Meet] Processed remote users:', {
        count: this.remoteUsers.length,
        users: this.remoteUsers.map(u => ({ uid: u.uid, hasVideo: u.hasVideo, hasVideoTrack: !!u.videoTrack }))
      });
      
      if (mediaType === "video") {
        console.log(`[Meet] Processing video publish from ${user.uid}`);
        
        // FIXED: Enhanced video processing with immediate directive creation
        const client = this.$refs.ar?.getClient?.();
        if (client) {
          const currentUser = client.remoteUsers.find(u => u.uid === user.uid);
          if (!currentUser || !currentUser.hasVideo || !currentUser.videoTrack) {
            console.warn(`[Meet] User ${user.uid} video validation failed - aborting`);
            return;
          }
          
          // Set optimal stream quality immediately
          try {
            console.log(`[Meet] Configuring optimal stream for ${user.uid}`);
            client.setRemoteVideoStreamType(user.uid, 0); // High stream first
          } catch (streamErr) {
            console.warn('[Meet] Stream configuration failed:', streamErr);
          }
        }
        
        // FIXED: Immediate directive creation with multiple fallbacks
        this.createVideoDirectiveForUser(user.uid);
        
        // Backup attempts with progressive delays
        setTimeout(() => this.createVideoDirectiveForUser(user.uid), 500);
        setTimeout(() => this.createVideoDirectiveForUser(user.uid), 1200);
      }
      
      if (mediaType === "audio") {
        this.handleGetRemoteVolumeLevelList();
      }
      
      // Force UI update
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    
    // FIXED: New method for reliable video directive creation
    createVideoDirectiveForUser(uid) {
      console.log(`[Meet] Creating video directive for ${uid}`);
      
      const client = this.$refs.ar?.getClient?.();
      if (!client) return;
      
      // Verify user still has video
      const targetUser = client.remoteUsers.find(u => u.uid === uid);
      if (!targetUser || !targetUser.hasVideo || !targetUser.videoTrack) {
        console.warn(`[Meet] User ${uid} no longer has valid video - skipping directive creation`);
        return;
      }
      
      // Force video receiver update
      if (this.$refs.videoReceiver) {
        this.$refs.videoReceiver.$forceUpdate?.();
        
        // Get fresh directives
        setTimeout(() => {
          const freshDirectives = this.$refs.videoReceiver?.getVideoDirectiveList?.() || [];
          console.log(`[Meet] Fresh directives for ${uid}:`, {
            total: freshDirectives.length,
            forUser: freshDirectives.filter(d => d?.uid === uid).length,
            allUids: freshDirectives.map(d => d?.uid)
          });
          
          if (freshDirectives.length > 0) {
            // Check if we have a directive for this specific user
            const userDirective = freshDirectives.find(d => d?.uid === uid);
            if (userDirective) {
              console.log(`[Meet] Successfully created directive for ${uid}`);
              this.handleRemoteVideoReady(freshDirectives);
            } else {
              console.warn(`[Meet] No directive found for ${uid} in fresh list`);
              // Try to force resubscribe as last resort
              this.forceResubscribeUser(targetUser);
            }
          }
        }, 200);
      }
    },
    handleUserLeft(user) {
      if (!user || user.uid == null) {
        console.warn('[Meet] Invalid user left:', user);
        return;
      }
      
      const remoteUsers = this.$refs.ar?.getRemoteUsers?.() || [];
      
      // Deep filtering with null safety for user left event
      this.remoteUsers = remoteUsers
        .filter(u => u && u.uid != null && u.uid !== undefined)
        .filter(u => !this.openScreenSharing || u.uid !== this.shareScreenUID)
        .map(u => ({
          uid: u.uid,
          hasVideo: u.hasVideo !== false,
          hasAudio: u.hasAudio !== false,
          displayName: u.displayName || null,
          userAccount: u.userAccount || null,
          finalDisplayName: this.getParticipantDisplayName(u)
        }));
      
      // Remove from pinned participants
      this.pinnedParticipants.delete(user.uid);
      
      // Clear active speaker if this user was speaking
      if (this.activeSpeakerUid === user.uid) {
        this.activeSpeakerUid = null;
      }
      
      this.$message?.info?.(`${user.uid} left the meeting`);
    },
    handleVideoReady(localVideo /* , info */) {
      this.localDirective = localVideo;
      // Show local video also in a top banner element if you have one:
      try {
        const videoTrack = this.$refs.videoSender?.getTrack?.()?.getMediaStreamTrack?.();
        if (videoTrack && this.$refs.localCameraPlayer) {
          this.$refs.localCameraPlayer.srcObject = new MediaStream([videoTrack]);
        }
      } catch {
      }
    },
    handleVideoClose() {
      this.localDirective = null;
    },
    handleScreenVideoReady(screenVideo) {
      this.youAreShareScreening = true;
      this.screenShareDirective = screenVideo;
      this.screenSharePresenter = 'You';
    },
    
    handleScreenVideoClose() {
      this.youAreShareScreening = false;
      this.screenShareDirective = null;
      this.screenSharePresenter = '';
    },
    
    handleScreenVideoFailed() {
      this.handleScreenVideoClose();
      this.openScreenSharing = false;
      this.errorMsg = 'Failed to share screen. Please try again.';
    },
    handleRemoteVideoReady(videoDirectiveList) {
      console.log('[Meet] Remote video ready with directives:', videoDirectiveList?.length || 0);
      
      if (videoDirectiveList && Array.isArray(videoDirectiveList) && videoDirectiveList.length > 0) {
        // FIXED: Enhanced directive validation and UID mapping
        const validDirectives = videoDirectiveList.filter(directive => {
          if (!directive || !directive.uid) {
            console.warn('[Meet] Invalid directive found:', directive);
            return false;
          }
          
          // Verify this directive corresponds to an actual remote user
          const hasCorrespondingUser = this.remoteUsers.some(user => user.uid === directive.uid);
          if (!hasCorrespondingUser) {
            console.warn(`[Meet] Directive for ${directive.uid} has no corresponding user`);
            return false;
          }
          
          return true;
        });
        
        this.remoteDirectiveList = validDirectives;
        
        console.log('[Meet] Updated remoteDirectiveList:', {
          count: this.remoteDirectiveList.length,
          directives: this.remoteDirectiveList.map(d => ({ uid: d?.uid, hasVideo: !!d })),
          correspondingUsers: this.remoteUsers.map(u => ({ uid: u.uid, hasVideo: u.hasVideo }))
        });
        
        // FIXED: Enhanced refresh mechanism with proper timing
        this.$forceUpdate();
        
        // Ensure video elements are properly bound
        this.$nextTick(() => {
          setTimeout(() => {
            console.log('[Meet] Secondary video refresh with element binding check');
            
            // Check if video elements are properly bound
            this.remoteDirectiveList.forEach(directive => {
              const videoElement = document.querySelector(`video[v-player="${directive}"]`);
              if (!videoElement) {
                console.warn(`[Meet] Video element not found for directive ${directive.uid}`);
              } else {
                console.log(`[Meet] Video element found for ${directive.uid}`);
              }
            });
            
            this.$forceUpdate();
          }, 200);
        });
      } else {
        console.warn('[Meet] Empty or invalid video directive list');
        // FIXED: Enhanced retry mechanism with better timing
        setTimeout(() => {
          const videoReceiver = this.$refs.videoReceiver;
          if (videoReceiver && videoReceiver.getVideoDirectiveList) {
            const freshDirectives = videoReceiver.getVideoDirectiveList();
            console.log('[Meet] Retry attempt - fresh directives:', freshDirectives?.length || 0);
            
            if (freshDirectives && freshDirectives.length > 0) {
              console.log('[Meet] Got fresh directives on retry:', freshDirectives.length);
              this.handleRemoteVideoReady(freshDirectives);
            } else {
              // Final attempt: Force video receiver refresh
              console.log('[Meet] Final attempt: forcing video receiver refresh');
              videoReceiver.$forceUpdate?.();
              
              setTimeout(() => {
                const finalDirectives = videoReceiver.getVideoDirectiveList();
                if (finalDirectives && finalDirectives.length > 0) {
                  this.handleRemoteVideoReady(finalDirectives);
                }
              }, 1000);
            }
          }
        }, 800);
      }
    },

    // FIXED: Enhanced force create video directive with better validation
    forceCreateVideoDirectiveFor(uid, userIndex) {
      console.log(`[Meet] Force creating video directive for ${uid} at index ${userIndex}`);
      
      const client = this.$refs.ar?.getClient?.();
      if (!client) {
        console.warn('[Meet] No client available for force create');
        return;
      }
      
      // Use the new reliable method
      this.createVideoDirectiveForUser(uid);
    },
    
    // FIXED: Enhanced resubscribe method with better error handling
    async forceResubscribeUser(user) {
      console.log(`[Meet] Force resubscribing user ${user.uid}`);
      
      const client = this.$refs.ar?.getClient?.();
      if (!client || !user.hasVideo || !user.videoTrack) {
        console.warn(`[Meet] Cannot resubscribe ${user.uid} - invalid state`);
        return;
      }
      
      try {
        // Unsubscribe first
        await client.unsubscribe(user, 'video');
        console.log(`[Meet] Unsubscribed from ${user.uid}`);
        
        // Wait then resubscribe
        setTimeout(async () => {
          try {
            await client.subscribe(user, 'video');
            console.log(`[Meet] Resubscribed to ${user.uid}`);
            
            // Force directive refresh after resubscription
            setTimeout(() => {
              this.createVideoDirectiveForUser(user.uid);
            }, 500);
            
          } catch (resubErr) {
            console.error(`[Meet] Resubscription failed for ${user.uid}:`, resubErr);
          }
        }, 1000);
        
      } catch (unsubErr) {
        console.error(`[Meet] Unsubscription failed for ${user.uid}:`, unsubErr);
      }
    },
    
    // FIXED: Add video loading event handlers
    onVideoLoaded(uid) {
      console.log(`[Meet] Video loaded successfully for ${uid}`);
      
      // Verify the video element is properly displaying
      setTimeout(() => {
        const videoElement = this.$refs[`remoteVideo-${uid}`];
        if (videoElement && videoElement[0]) {
          const video = videoElement[0];
          console.log(`[Meet] Video element stats for ${uid}:`, {
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            readyState: video.readyState,
            paused: video.paused
          });
          
          if (video.videoWidth === 0 || video.videoHeight === 0) {
            console.warn(`[Meet] Video ${uid} has zero dimensions - attempting refresh`);
            this.createVideoDirectiveForUser(uid);
          }
        }
      }, 100);
    },
    
    onVideoError(uid, event) {
      console.error(`[Meet] Video error for ${uid}:`, event);
      
      // Attempt recovery
      setTimeout(() => {
        console.log(`[Meet] Attempting video recovery for ${uid}`);
        this.createVideoDirectiveForUser(uid);
      }, 1000);
    },

    handleAudioTrackCreated() {
      let id;
      const cb = () => {
        try {
          const track = this.$refs.audioSender?.getTrack?.();
          this.localVolumeLevel = track?.getVolumeLevel ? track.getVolumeLevel() : 0;
        } catch {
          this.localVolumeLevel = 0;
        }
        id = window.requestAnimationFrame(cb);
      };
      id = window.requestAnimationFrame(cb);
      this.$once("hook:beforeDestroy", () => window.cancelAnimationFrame(id));
    },
    handleGetRemoteVolumeLevelList() {
      let id;
      const cb = () => {
        const levels = this.$refs.audioReceiver?.getVolumeLevel?.() || [];
        this.remoteVolumeLevelList = levels;
        
        // Update active speaker based on volume levels - with null checks
        if (levels.length > 0 && this.remoteUsers && Array.isArray(this.remoteUsers)) {
          const maxLevel = Math.max(...levels);
          const maxIndex = levels.indexOf(maxLevel);
          
          // FIXED: Enhanced null safety for audio level processing
          if (maxLevel > 0.3 && maxIndex >= 0 && maxIndex < this.remoteUsers.length) {
            const targetUser = this.remoteUsers[maxIndex];
            if (targetUser && targetUser.uid != null && targetUser.uid !== undefined) {
              this.updateActiveSpeaker(targetUser.uid);
            }
          } else if (this.localVolumeLevel > 0.3 && !this.mute) {
            this.updateActiveSpeaker('local');
          }
        }
        
        id = window.requestAnimationFrame(cb);
      };
      id = window.requestAnimationFrame(cb);
      this.$once("hook:beforeDestroy", () => window.cancelAnimationFrame(id));
    },
    handleCheckRemoteUserAudioMuteStatus() {
      let id;
      const cb = () => {
        this.remoteMuteStatus = this.$refs.audioReceiver?.getUserMuteStatus?.() || [];
        id = window.requestAnimationFrame(cb);
      };
      id = window.requestAnimationFrame(cb);
      this.$once("hook:beforeDestroy", () => window.cancelAnimationFrame(id));
    },

    // Control handlers
    handleMute() {
      this.mute = !this.mute;
      this.$message?.info?.(this.mute ? 'Microphone turned off' : 'Microphone turned on');
    },
    
    handleCamera() {
      this.cameraIsClosed = !this.cameraIsClosed;
      this.$message?.info?.(this.cameraIsClosed ? 'Camera turned off' : 'Camera turned on');
    },
    
    handleShareScreen() {
      if (!this.youAreShareScreening && this.openScreenSharing) {
        this.$message?.warning?.('Someone else is sharing. You will replace their screen share.');
      } else if (this.youAreShareScreening && !this.openScreenSharing) {
        this.$message?.info?.('You will stop sharing your screen.');
      }
      this.openScreenSharing = !this.openScreenSharing;
    },
    handleStreamFallback(uid, type) {
      if (type === "recover") {
        this.streamFallbackList = this.streamFallbackList.filter(e => e !== uid);
      } else if (type === "fallback") {
        this.streamFallbackList = [...new Set([...this.streamFallbackList, uid])];
      }
    },
    
    // New event handlers for better connection diagnostics
    handleConnectionStateChange(newState, prevState, reason) {
      console.log('[Meet] Connection state changed:', {
        from: prevState,
        to: newState,
        reason: reason,
        timestamp: Date.now()
      });
      
      if (newState === 'DISCONNECTED') {
        this.inMeeting = false;
        if (reason === 'SERVER_ERROR') {
          this.errorMsg = 'خطأ في الخادم - انقطع الاتصال';
          this.$message?.error?.('انقطع الاتصال مع الخادم');
        } else if (reason === 'NETWORK_ERROR') {
          this.errorMsg = 'خطأ في الشبكة - تحقق من الاتصال';
          this.$message?.error?.('مشكلة في اتصال الشبكة');
        }
      } else if (newState === 'CONNECTING') {
        this.errorMsg = 'جارِ الاتصال بالخادم...';
      } else if (newState === 'CONNECTED') {
        this.errorMsg = null;
      }
    },
    
    handleSDKException(exception) {
      console.error('[Meet] SDK Exception:', exception);
      
      if (exception.code === 'NETWORK_ERROR') {
        this.errorMsg = 'مشكلة في الشبكة - تحقق من جودة الاتصال';
        this.$message?.warning?.('جودة الشبكة ضعيفة - قد يتأثر جودة الصوت والفيديو');
      } else if (exception.code === 'AUDIO_INPUT_LEVEL_TOO_LOW') {
        console.warn('[Meet] Microphone level too low');
      } else if (exception.code === 'SEND_VIDEO_BITRATE_TOO_LOW') {
        console.warn('[Meet] Video bitrate too low');
      } else {
        console.warn('[Meet] SDK Exception:', exception.code, exception.msg);
      }
    },
    
    handleLeave() {
      if (!this.inMeeting) {
        this.$message?.error?.('You have not joined any meetings');
        return;
      }
      
      // Show confirmation dialog
      /*if (!confirm('Are you sure you want to leave the meeting?')) {
        return;
      }*/
      
      this.$refs.ar.leave?.().then(() => {
        this.inMeeting = false;
        this.remoteUsers = [];
        this.meetingStartTime = null;
        this.$message?.success?.('Successfully left the meeting');
        this.$emit('leave-meeting');
      }).catch(this.handleError);
    }
  }
};
</script>

<style scoped>
/* Main container */
.meet-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  background: #202124;
  color: #ffffff;
  font-family: 'Google Sans', Roboto, Arial, sans-serif;
  overflow: hidden;
}

/* Meeting interface */
.meet-interface {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Meeting header */
.meeting-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(32, 33, 36, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 100;
}

.meeting-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meeting-title {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
}

.participant-count {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #9aa0a6;
}

.participant-count i {
  font-size: 12px;
}

.meeting-controls-header {
  display: flex;
  gap: 8px;
}

.header-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.header-btn.active {
  background: #8b3d97;
}

/* Video grid container */
.video-grid-container {
  flex: 1;
  display: flex;
  position: relative;
  transition: all 0.3s ease;
}

.video-grid-container.with-sidebar {
  margin-right: 320px;
}

/* Screen share area */
.screen-share-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
}

.screen-share-tile {
  flex: 1;
  background: #000000;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  min-height: 0;
}

.screen-share-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
  z-index: 10;
}

.presenter-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 14px;
}

.presenter-info i {
  color: #8b3d97;
}

.stop-sharing-btn {
  margin-left: auto;
  padding: 8px 16px;
  background: #ea4335;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
}

.stop-sharing-btn:hover {
  background: #d33b2c;
}

.minimized-videos {
  display: flex;
  gap: 12px;
  height: 120px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.minimized-videos::-webkit-scrollbar {
  display: none;
}

/* Video grid layouts */
.video-grid {
  flex: 1;
  display: grid;
  padding: 16px;
  gap: 12px;
  align-content: center;
  justify-content: center;
}

.video-grid.grid-1 {
  grid-template-columns: 1fr;
  max-width: 800px;
  margin: 0 auto;
}

.video-grid.grid-2 {
  grid-template-columns: repeat(2, 1fr);
  max-width: 1200px;
  margin: 0 auto;
}

.video-grid.grid-4 {
  grid-template-columns: repeat(2, 1fr);
  max-width: 1200px;
  margin: 0 auto;
}

.video-grid.grid-6 {
  grid-template-columns: repeat(3, 1fr);
}

.video-grid.grid-9 {
  grid-template-columns: repeat(3, 1fr);
}

.video-grid.grid-many {
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}

/* Video tiles */
.video-tile, .mini-video-tile {
  position: relative;
  background: #000000;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16/9;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 3px solid transparent;
}

/* Video player styling */
.video-player {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000000;
}

.video-tile:hover, .mini-video-tile:hover {
  transform: scale(1.02);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.video-tile.active-speaker, .mini-video-tile.active-speaker {
  border-color: #8b3d97;
  box-shadow: 0 0 20px rgba(66, 133, 244, 0.4);
}

.video-tile.pinned, .mini-video-tile.pinned {
  border-color: #34a853;
}

.local-tile {
  position: relative;
}

.mini-video-tile {
  width: 160px;
  height: 90px;
  flex-shrink: 0;
}

.local-mini {
  border: 2px solid #1a73e8;
}

/* Video overlay */
.video-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 80%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px;
  z-index: 10;
}

.participant-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
}

.participant-info .name {
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
}

.status-indicators {
  display: flex;
  gap: 4px;
}

.status-indicators i {
  font-size: 12px;
}

.status-indicators .muted {
  color: #ea4335;
}

.status-indicators .video-off {
  color: #ea4335;
}

.status-indicators .active {
  color: #34a853;
}

/* Pin button */
.pin-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.video-tile:hover .pin-btn, .mini-video-tile:hover .pin-btn {
  opacity: 1;
}

.pin-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.pin-btn .pinned {
  color: #34a853;
}

/* Volume indicator */
.volume-indicator {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  height: 3px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
}

.volume-bar {
  height: 100%;
  background: #8b3d97;
  transition: width 0.1s ease;
  border-radius: 2px;
}

/* Video placeholder */
.video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #3c4043;
}

.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #5f6368;
  font-size: 32px;
  color: #ffffff;
  font-weight: 500;
}

.mini-video-tile .avatar {
  width: 40px;
  height: 40px;
  font-size: 16px;
}

/* Participants sidebar */
.participants-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  height: 100vh;
  background: rgba(32, 33, 36, 0.95);
  backdrop-filter: blur(10px);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 200;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
}

.close-sidebar {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 16px;
  background: transparent;
  color: #9aa0a6;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-sidebar:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.participants-list {
  flex: 1;
  padding: 16px 0;
  overflow-y: auto;
}

.participant-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  transition: all 0.2s ease;
}

.participant-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.participant-item.local {
  background: rgba(26, 115, 232, 0.1);
}

.participant-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #5f6368;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  flex-shrink: 0;
}

.participant-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.participant-details .name {
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
}

.participant-details .role {
  font-size: 12px;
  color: #9aa0a6;
}

.participant-status {
  display: flex;
  gap: 8px;
  align-items: center;
}

.participant-status i {
  font-size: 16px;
}

/* Meeting toolbar */
.meeting-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(32, 33, 36, 0.95);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 100;
}

.toolbar-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-section.center {
  gap: 16px;
}

.meeting-time {
  font-size: 14px;
  color: #9aa0a6;
  font-variant-numeric: tabular-nums;
}

/* Google Meet Style Control buttons */
.control-btn {
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: #3c4043;
  color: #e8eaed;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
  margin: 0 8px;
  font-size: 20px;
  position: relative;
  box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
}

.control-btn:hover {
  background: #5f6368;
  box-shadow: 0 1px 3px 0 rgba(60, 64, 67, 0.3), 0 4px 8px 3px rgba(60, 64, 67, 0.15);
}

.control-btn.active {
  background: #8b3d97;
  color: white;
}

.control-btn.muted {
  background: #ea4335;
  color: white;
}

.control-btn.disabled {
  background: #ea4335;
  color: white;
}

/* Leave button - oval shape like Google Meet */
.leave-btn {
  width: 72px;
  height: 56px;
  border-radius: 28px;
  background: #ea4335;
  color: white;
  box-shadow: 0 1px 2px 0 rgba(234, 67, 53, 0.3), 0 2px 6px 2px rgba(234, 67, 53, 0.15);
}

.leave-btn:hover {
  background: #d93025;
  box-shadow: 0 2px 4px 0 rgba(217, 48, 37, 0.3), 0 4px 12px 4px rgba(217, 48, 37, 0.15);
}

/* Google Meet specific components */
.participant-count {
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: #8b3d97;
  color: white;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 12px;
  min-width: 20px;
  text-align: center;
  line-height: 1.2;
  border: 2px solid #202124;
}

.btn-indicator {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 8px;
  height: 8px;
  background: #34a853;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .control-btn {
    min-width: 70px;
    height: 56px;
    padding: 6px 10px;
  }
  
  .enhanced-btn {
    min-width: 75px;
    padding: 8px 12px;
  }
  
  .btn-icon {
    font-size: 18px;
    margin-bottom: 2px;
  }
  
  .btn-label {
    font-size: 10px;
  }
}

/* Error toast */
.error-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #ea4335;
  color: #ffffff;
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 400px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  cursor: pointer;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateX(-50%) translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

.close-error {
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  margin-left: 8px;
}

/* Responsive design */
@media (max-width: 1200px) {
  .video-grid-container.with-sidebar {
    margin-right: 280px;
  }
  
  .participants-sidebar {
    width: 280px;
  }
}

@media (max-width: 768px) {
  .meeting-header {
    padding: 12px 16px;
  }
  
  .meeting-title {
    font-size: 16px;
  }
  
  .video-grid {
    padding: 12px;
    gap: 8px;
  }
  
  .video-grid.grid-2,
  .video-grid.grid-4,
  .video-grid.grid-6,
  .video-grid.grid-9 {
    grid-template-columns: 1fr;
  }
  
  .video-grid.grid-many {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  
  .meeting-toolbar {
    padding: 12px 16px;
  }
  
  .toolbar-section.center {
    gap: 12px;
  }
  
  .control-btn {
    width: 48px;
    height: 48px;
    font-size: 18px;
  }
  
  .video-grid-container.with-sidebar {
    margin-right: 0;
  }
  
  .participants-sidebar {
    width: 100vw;
  }
  
  .minimized-videos {
    height: 100px;
  }
  
  .mini-video-tile {
    width: 120px;
    height: 68px;
  }
}

@media (max-width: 480px) {
  .video-grid {
    padding: 8px;
    gap: 6px;
  }
  
  .control-btn {
    width: 44px;
    height: 44px;
    font-size: 16px;
  }
  
  .toolbar-section.center {
    gap: 8px;
  }
  
  .meeting-time {
    font-size: 12px;
  }
  
  .participant-count {
    font-size: 12px;
  }
}

/* Smooth transitions */
.video-tile, .mini-video-tile, .control-btn, .header-btn {
  will-change: transform;
}

/* Focus states for accessibility */
.control-btn:focus,
.header-btn:focus,
.pin-btn:focus {
  outline: 2px solid #1a73e8;
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: more) {
  .video-tile.active-speaker, .mini-video-tile.active-speaker {
    border-color: #ffffff;
    border-width: 4px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .video-tile, .mini-video-tile, .control-btn, .header-btn {
    transition: none;
  }
  
  .video-tile:hover, .mini-video-tile:hover {
    transform: none;
  }
}
</style>
